import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { useToast } from '../components/Toast';
import { Plus, Calendar, TrendingUp, Users, DollarSign, Edit2, Trash2, Copy, Eye, Ban, Gift, BarChart3, X } from 'lucide-react';
import { PromotionWizard } from '../components/PromotionWizard';
import { PromotionCalendar } from '../components/PromotionCalendar';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { promotionsService, settingsService, productsService } from '../services/firebaseService';

export const PromotionsV2 = () => {
  const toast = useToast();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [selectedPromoForPreview, setSelectedPromoForPreview] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [analytics, setAnalytics] = useState({
    active_promotions: 0,
    total_usage: 0,
    total_revenue: 0,
    total_discount: 0
  });

  // Setting: promos et récompenses non cumulables
  const [noPromoWithReward, setNoPromoWithReward] = useState(false);
  const [savingCumulSetting, setSavingCumulSetting] = useState(false);

  // Modal stats mensuelles
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [statsTotals, setStatsTotals] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Produits pour afficher les images sur les fiches promo
  const [productsMap, setProductsMap] = useState({});

  useEffect(() => {
    loadPromotions();
    loadCumulSetting();
    loadRealStats(); // Charger les vraies stats au démarrage
    loadProducts(); // Charger les produits pour les images
  }, []);

  // Charger les produits pour créer un map id -> product
  const loadProducts = async () => {
    try {
      // FIREBASE DIRECT
      const result = await productsService.getAll();
      const products = result?.products || [];
      const map = {};
      products.forEach(p => { map[p.id] = p; });
      setProductsMap(map);
    } catch (error) {
      console.error('Error loading products for promo cards:', error);
    }
  };

  // Charger les vraies stats pour les cards du haut
  const loadRealStats = async () => {
    try {
      // FIREBASE DIRECT
      const result = await promotionsService.getMonthlyStats(12);
      if (result.totals) {
        const totals = result.totals;
        setAnalytics(prev => ({
          ...prev,
          total_revenue: totals.total_revenue || 0,
          total_discount: totals.total_discount || 0
        }));
      }
    } catch (error) {
      console.error('Error loading real stats:', error);
    }
  };

  // Charger le setting de cumul
  const loadCumulSetting = async () => {
    try {
      // FIREBASE DIRECT
      const result = await settingsService.get();
      const settings = result?.settings || {};
      setNoPromoWithReward(settings.no_promo_with_reward || false);
    } catch (error) {
      console.error('Error loading cumul setting:', error);
    }
  };

  // Sauvegarder le setting de cumul
  const handleToggleCumul = async () => {
    setSavingCumulSetting(true);
    try {
      const newValue = !noPromoWithReward;
      // FIREBASE DIRECT
      await settingsService.update({ no_promo_with_reward: newValue });
      setNoPromoWithReward(newValue);
      toast.success(
        newValue ? 'Non-cumul activé' : 'Cumul autorisé',
        newValue
          ? 'Les promotions et récompenses ne sont plus cumulables'
          : 'Les promotions et récompenses peuvent être cumulées'
      );
    } catch (error) {
      toast.error('Erreur', 'Impossible de modifier le paramètre');
    } finally {
      setSavingCumulSetting(false);
    }
  };

  // Charger les stats mensuelles (vraies données depuis les commandes)
  const loadMonthlyStats = async () => {
    setLoadingStats(true);
    try {
      // FIREBASE DIRECT
      const result = await promotionsService.getMonthlyStats(12);
      setMonthlyStats(result.monthly || []);
      setStatsTotals(result.totals || null);
    } catch (error) {
      console.error('Error loading monthly stats:', error);
      toast.error('Erreur', 'Impossible de charger les statistiques');
    } finally {
      setLoadingStats(false);
    }
  };

  // Ouvrir le modal et charger les stats
  const handleOpenStatsModal = () => {
    setShowStatsModal(true);
    loadMonthlyStats();
  };

  // FIX: Calculer analytics quand promotions change
  useEffect(() => {
    if (promotions.length > 0) {
      calculateAnalytics(promotions);
    }
  }, [promotions]);

  const loadPromotions = async () => {
    setLoading(true);
    try {
      // FIREBASE DIRECT
      const result = await promotionsService.getAll();
      const promos = result.promotions || [];
      setPromotions(promos);
    } catch (error) {
      console.error('Error loading promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculer les analytics depuis les promos (seulement promos actives et utilisations)
  // Les vraies stats CA et remises viennent de loadRealStats()
  const calculateAnalytics = (promos) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    // Compter les promos actives (is_active ET dans les dates valides)
    const activeCount = promos.filter(p => {
      if (!p.is_active) return false;

      const startDate = p.start_date || '';
      const endDate = p.end_date || '';

      // Vérifier les dates
      if (startDate && startDate > today) return false;
      if (endDate && endDate < today) return false;

      return true;
    }).length;

    // Total utilisations (depuis les promos)
    const totalUsage = promos.reduce((sum, p) => sum + (p.usage_count || 0), 0);

    // Mettre à jour seulement active_promotions et total_usage
    // (total_revenue et total_discount viennent de loadRealStats)
    setAnalytics(prev => ({
      ...prev,
      active_promotions: activeCount,
      total_usage: totalUsage
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette promotion ?')) return;

    try {
      // FIREBASE DIRECT
      await promotionsService.delete(id);
      loadPromotions();
      toast.success('Promotion supprimée', 'La promotion a été supprimée');
    } catch (error) {
      toast.error('Erreur', 'Impossible de supprimer la promotion');
    }
  };

  const handleDuplicate = async (promo) => {
    const duplicate = {
      ...promo,
      name: `${promo.name} (copie)`,
      id: undefined,
      created_at: undefined,
      updated_at: undefined,
      usage_count: 0
    };
    setEditingPromo(duplicate);
    setShowWizard(true);
  };

  const handleToggle = async (promo) => {
    try {
      // FIREBASE DIRECT
      await promotionsService.toggle(promo.id, !promo.is_active);
      loadPromotions();
      toast.success(promo.is_active ? 'Promotion désactivée' : 'Promotion activée', '');
    } catch (error) {
      toast.error('Erreur', 'Impossible de modifier la promotion');
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      // Types du wizard
      'bogo': '🎁 BOGO',
      'percent_item': '💯 % Produit',
      'percent_category': '💯 % Catégorie',
      'conditional_discount': '🔢 Conditionnelle',
      'threshold': '🎯 Seuil',
      'shipping_free': '🚚 Livraison gratuite',
      'new_customer': '✨ Nouveau client',
      'happy_hour': '🌅 Happy Hour',
      'flash': '⚡ Flash',
      'seasonal': '🎉 Saisonnier',
      'promo_code': '🔖 Code promo',
      // Types legacy/alternatifs
      'percentage': '💯 % Réduction',
      'fixed_amount': '💰 € Fixe',
      'fixed_item': '💰 € Produit',
      'fixed_category': '💰 € Catégorie',
      'inactive_customer': '💤 Client inactif',
      'loyalty_multiplier': '⭐ Multiplicateur',
    };
    return labels[type] || type;
  };

  // Fonction utilitaire pour déterminer si c'est un pourcentage ou un montant
  const isPercentageType = (promo) => {
    // Si discount_type est explicitement défini, l'utiliser
    if (promo.discount_type === 'percentage') return true;
    if (promo.discount_type === 'fixed') return false;

    // Sinon déduire du type de promotion
    const percentTypes = [
      'percentage',
      'percent_item',
      'percent_category',
      'conditional_discount',
      'threshold'  // Les seuils sont généralement en %
    ];

    return percentTypes.includes(promo.type);
  };

  const getDiscountDisplay = (promo) => {
    // FIX: Gestion spéciale pour les types sans discount_value
    if (promo.type === 'bogo') {
      const buy = promo.buy_quantity || promo.bogo_buy_quantity || 1;
      const get = promo.get_quantity || promo.bogo_get_quantity || 1;
      return `${buy}+${get}`;
    }
    if (promo.type === 'shipping_free') {
      return 'Livr. gratuite';
    }
    if (promo.type === 'free_item') {
      return 'Offert';
    }

    // Cas standard
    const value = promo.discount_value || 0;
    const isPercent = isPercentageType(promo);
    return `${value}${isPercent ? '%' : '€'}`;
  };

  const getTypeColor = (type) => {
    const colors = {
      // Pourcentages - Bleu
      'percentage': 'bg-blue-100 text-blue-700',
      'percent_item': 'bg-blue-100 text-blue-700',
      'percent_category': 'bg-blue-100 text-blue-700',
      // Montants fixes - Vert
      'fixed_amount': 'bg-green-100 text-green-700',
      'fixed_item': 'bg-green-100 text-green-700',
      'fixed_category': 'bg-green-100 text-green-700',
      // BOGO - Violet
      'bogo': 'bg-purple-100 text-purple-700',
      // Conditionnelles - Jaune
      'conditional_discount': 'bg-yellow-100 text-yellow-700',
      'threshold': 'bg-yellow-100 text-yellow-700',
      // Happy Hour & Flash - Orange
      'happy_hour': 'bg-orange-100 text-orange-700',
      'flash': 'bg-orange-100 text-orange-700',
      // Spéciaux - Rose
      'new_customer': 'bg-pink-100 text-pink-700',
      'inactive_customer': 'bg-pink-100 text-pink-700',
      // Livraison - Cyan
      'shipping_free': 'bg-cyan-100 text-cyan-700',
      // Saisonnier - Rouge
      'seasonal': 'bg-red-100 text-red-700',
      // Code promo - Indigo
      'promo_code': 'bg-indigo-100 text-indigo-700',
      // Fidélité - Amber
      'loyalty_multiplier': 'bg-amber-100 text-amber-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div>
        <Header title="🎯 Promotions & Offres" subtitle="Moteur de promotions Family's" />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="🎯 Promotions & Offres" subtitle="Moteur de promotions Family's" />
      
      <div className="p-6 space-y-6">
        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-white/80" />
                <span className="text-3xl font-black">{analytics.active_promotions}</span>
              </div>
              <p className="text-sm text-white/80">Promos actives</p>
            </div>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-white/80" />
                <span className="text-3xl font-black">{analytics.total_usage}</span>
              </div>
              <p className="text-sm text-white/80">Utilisations</p>
            </div>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-8 h-8 text-white/80" />
                <span className="text-3xl font-black">{analytics.total_revenue}€</span>
              </div>
              <p className="text-sm text-white/80">CA généré</p>
            </div>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white">
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-8 h-8 text-white/80" />
                <span className="text-3xl font-black">{analytics.total_discount}€</span>
              </div>
              <p className="text-sm text-white/80">Remises totales</p>
            </div>
          </Card>
        </div>

        {/* Toggle Cumul Promos/Récompenses */}
        <Card className={`border-2 ${noPromoWithReward ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${noPromoWithReward ? 'bg-red-100' : 'bg-gray-100'}`}>
                {noPromoWithReward ? <Ban className="w-6 h-6 text-red-600" /> : <Gift className="w-6 h-6 text-gray-500" />}
              </div>
              <div>
                <h3 className="font-bold text-gray-800">
                  {noPromoWithReward ? '🚫 Promos et Récompenses NON cumulables' : '✅ Promos et Récompenses cumulables'}
                </h3>
                <p className="text-sm text-gray-600">
                  {noPromoWithReward
                    ? 'Un client ne peut pas utiliser une récompense (jeu Surprise du Jour) en même temps qu\'une promotion'
                    : 'Les clients peuvent cumuler leurs récompenses du jeu avec les promotions en cours'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleCumul}
              disabled={savingCumulSetting}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                noPromoWithReward ? 'bg-red-500' : 'bg-gray-300'
              } ${savingCumulSetting ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-md ${
                  noPromoWithReward ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <Button
              variant={activeTab === 'list' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('list')}
            >
              📋 Liste
            </Button>
            <Button
              variant={activeTab === 'calendar' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('calendar')}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Calendrier
            </Button>
            <Button
              variant={activeTab === 'preview' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('preview')}
            >
              <Eye className="w-4 h-4 mr-2" />
              Aperçu App
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleOpenStatsModal}>
              <BarChart3 className="w-4 h-4 mr-2" />
              Stats mensuelles
            </Button>
            <Button onClick={() => { setEditingPromo(null); setShowWizard(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Créer une promotion
            </Button>
          </div>
        </div>

        {/* Content based on tab */}
        {activeTab === 'list' && (
          <div className="space-y-4">
            {promotions.length === 0 ? (
              <Card className="text-center py-12">
                <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Aucune promotion créée</p>
                <Button onClick={() => setShowWizard(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer ma première promotion
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {promotions.map((promo) => {
                  const isActive = promo.is_active;
                  const now = new Date().toISOString().split('T')[0];
                  const isExpired = promo.end_date && promo.end_date < now;
                  const isUpcoming = promo.start_date && promo.start_date > now;
                  
                  let statusColor = 'border-gray-300 bg-gray-50';
                  let statusText = 'Brouillon';
                  
                  if (isActive && !isExpired && !isUpcoming) {
                    statusColor = 'border-green-500 bg-green-50';
                    statusText = '✅ Active';
                  } else if (isExpired) {
                    statusColor = 'border-red-400 bg-red-50';
                    statusText = '⏰ Expirée';
                  } else if (isUpcoming) {
                    statusColor = 'border-blue-400 bg-blue-50';
                    statusText = '📅 À venir';
                  } else if (!isActive) {
                    statusColor = 'border-orange-400 bg-orange-50';
                    statusText = '⏸️ Inactive';
                  }
                  
                  // Récupérer l'image du premier produit ciblé (si applicable)
                  const targetProductIds = promo.product_ids || [];
                  const firstProductId = targetProductIds[0];
                  const firstProduct = firstProductId ? productsMap[firstProductId] : null;
                  const productImage = firstProduct?.image || firstProduct?.image_url;

                  return (
                    <Card key={promo.id} className={`border-2 ${statusColor}`}>
                      <div className="p-4">
                        {/* Image produit si promo sur produit(s) spécifique(s) */}
                        {productImage && targetProductIds.length > 0 && (
                          <div className="mb-3 relative">
                            <img
                              src={productImage}
                              alt={firstProduct?.name || 'Produit'}
                              className="w-full h-24 object-contain rounded-lg bg-gray-50"
                            />
                            {targetProductIds.length > 1 && (
                              <span className="absolute bottom-1 right-1 text-xs bg-black/60 text-white px-2 py-0.5 rounded">
                                +{targetProductIds.length - 1} autre{targetProductIds.length > 2 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Header */}
                        <div className="flex justify-between items-start mb-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${getTypeColor(promo.type)}`}>
                            {getTypeLabel(promo.type)}
                          </span>
                          <span className="text-2xl font-black text-primary">
                            {getDiscountDisplay(promo)}
                          </span>
                        </div>

                        {/* Name & Description */}
                        <h3 className="font-bold text-gray-900 mb-1">{promo.name}</h3>
                        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{promo.description}</p>
                        
                        {/* Badge */}
                        {promo.badge_text && (
                          <div 
                            className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-3"
                            style={{ backgroundColor: promo.badge_color || '#FF6B35' }}
                          >
                            {promo.badge_text}
                          </div>
                        )}
                        
                        {/* Dates */}
                        <div className="text-xs text-gray-400 mb-3">
                          🗓️ {promo.start_date} → {promo.end_date}
                        </div>
                        
                        {/* Stats */}
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                          <span>📊 {promo.usage_count || 0} utilisations</span>
                          {promo.usage_limit && <span>/ {promo.usage_limit} max</span>}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant={isActive ? 'outline' : 'primary'}
                            onClick={() => handleToggle(promo)}
                            className="flex-1"
                          >
                            {isActive ? '⏸️ Désactiver' : '▶️ Activer'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => {
                              setEditingPromo(promo);
                              setShowWizard(true);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDuplicate(promo)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="danger" 
                            onClick={() => handleDelete(promo.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'calendar' && (
          <PromotionCalendar
            promotions={promotions}
            onPromoClick={(promo) => {
              setEditingPromo(promo);
              setShowWizard(true);
            }}
          />
        )}

        {activeTab === 'preview' && (
          <Card className="p-6">
            <h3 className="text-lg font-bold mb-4">📱 Aperçu dans l'app</h3>
            <div className="bg-gray-100 rounded-xl p-4 max-w-sm mx-auto">
              <p className="text-center text-gray-500 mb-4">Promos en cours visibles par les clients :</p>
              <div className="space-y-3">
                {promotions.filter(p => p.is_active && p.is_visible_in_app).map(promo => (
                  <div 
                    key={promo.id} 
                    className="bg-white rounded-lg p-3 shadow"
                  >
                    <div className="flex justify-between items-center">
                      <span 
                        className="text-xs font-bold px-2 py-1 rounded text-white"
                        style={{ backgroundColor: promo.badge_color || '#FF6B35' }}
                      >
                        {promo.badge_text || promo.name}
                      </span>
                      <span className="font-bold text-primary">
                        -{getDiscountDisplay(promo)}
                      </span>
                    </div>
                  </div>
                ))}
                {promotions.filter(p => p.is_active && p.is_visible_in_app).length === 0 && (
                  <p className="text-center text-gray-400 text-sm">Aucune promo visible</p>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <PromotionWizard
          isOpen={showWizard}
          onClose={() => {
            setShowWizard(false);
            setEditingPromo(null);
          }}
          promotion={editingPromo}
          onSuccess={() => {
            setShowWizard(false);
            setEditingPromo(null);
            loadPromotions();
          }}
        />
      )}

      {/* Modal Stats Mensuelles */}
      {showStatsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <BarChart3 className="w-6 h-6" />
                    Statistiques Promotions
                  </h2>
                  <p className="text-purple-200 mt-1">Données réelles des 12 derniers mois</p>
                </div>
                <button
                  onClick={() => setShowStatsModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {loadingStats ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <>
                  {/* Totaux */}
                  {statsTotals && (
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-green-700">{statsTotals.total_revenue?.toFixed(0) || 0}€</p>
                        <p className="text-sm text-green-600">CA avec promo</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-red-700">{statsTotals.total_discount?.toFixed(0) || 0}€</p>
                        <p className="text-sm text-red-600">Remises données</p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-blue-700">{statsTotals.total_usage || 0}</p>
                        <p className="text-sm text-blue-600">Utilisations</p>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-purple-700">{statsTotals.total_orders || 0}</p>
                        <p className="text-sm text-purple-600">Commandes</p>
                      </div>
                    </div>
                  )}

                  {/* Tableau mensuel */}
                  <div className="border rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Mois</th>
                          <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">CA avec promo</th>
                          <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">Remises</th>
                          <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">Utilisations</th>
                          <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">Commandes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {monthlyStats.map((month, idx) => (
                          <tr key={month.month} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 font-medium text-gray-900">{month.month_label}</td>
                            <td className="px-4 py-3 text-right text-green-600 font-semibold">
                              {month.revenue_with_promo?.toFixed(2) || '0.00'}€
                            </td>
                            <td className="px-4 py-3 text-right text-red-600 font-semibold">
                              {month.total_discount?.toFixed(2) || '0.00'}€
                            </td>
                            <td className="px-4 py-3 text-right text-blue-600 font-semibold">
                              {month.usage_count || 0}
                            </td>
                            <td className="px-4 py-3 text-right text-purple-600 font-semibold">
                              {month.orders_count || 0}
                            </td>
                          </tr>
                        ))}
                        {monthlyStats.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                              Aucune donnée disponible
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-xs text-gray-500 mt-4 text-center">
                    💡 Ces données sont calculées depuis les commandes réelles avec promotions appliquées
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
